import { StackFrame } from "@sentry/solid";

import CreditLine from "@repo/kikoff-models/lending/CreditLine";
import Subscription from "@repo/kikoff-models/Subscription";
import Analytics from "@repo/utils/Analytics";
import UObject from "@repo/utils/UObject";
import UString from "@repo/utils/UString";
import UTuple from "@repo/utils/UTuple";
import UType from "@repo/utils/UType";

import { OverlayKey } from "~/overlays";

export const formatted = Symbol("formatted");
export const actions = Symbol("actions");

type ActionType =
  | "impression"
  // Maps to `: Tap`
  | "click"
  | "attempt"
  | "success"
  | "failure"
  | "debug";

type AsyncActions<P extends Analytics.Properties> = Record<
  "attempt" | "success" | "failure",
  P
> & {
  failure: {
    errors: string[];
  };
};

export type EventsSchema = {
  [key: string]: {
    [key: string]: EventsSchema[string];
    [formatted]: string;
    [actions]?: {
      [A in ActionType]?: Analytics.Properties;
    };
  };
};

export type FlattenEvents<E extends EventsSchema> =
  // @ts-expect-error excessive type instantiation
  UObject.FromEntryUnion<FlattenEventsImpl<E>>;

type FlattenEventsImpl<
  E extends EventsSchema,
  Parents extends string[] = [],
> = UObject.Value<{
  [K in Exclude<keyof E, symbol>]:
    | (E[K] extends { readonly [actions]: infer Actions }
        ? UObject.Entry<{
            [A in keyof Actions as `${UTuple.Join<
              [...Parents, E[K][typeof formatted]],
              " - "
            >}: ${UString.Capitalize<UType.Narrow<A, string>>}`]: Actions[A];
          }>
        : never)
    | FlattenEventsImpl<E[K], [...Parents, E[K][typeof formatted]]>;
}>;

const events = {
  sentry: {
    [formatted]: "Sentry",
    send: {
      [formatted]: "Send",
      [actions]: {} as {
        debug: {
          message: string | undefined;
          filestack: (string | undefined)[] | undefined;
          frames: UObject.Resolve<StackFrame>[] | undefined;
          suppress: boolean;
        };
      },
    },
  },
  page: {
    [formatted]: "Page",
    [actions]: {
      impression: {} as
        | {
            initial: true;
          }
        | {
            initial: false;
            from: string;
          },
    },
  },
  overlay: {
    [formatted]: "Overlay",
    [actions]: {} as {
      impression: {
        key: OverlayKey;
      };
    },
  },
  notFoundPage: {
    [formatted]: "404 Page",
    [actions]: {} as {
      impression: {};
    },
  },
  login: {
    [formatted]: "Login",
    [actions]: {} as AsyncActions<{}>,
    submitButton: {
      [formatted]: "Submit Button",
      [actions]: {} as {
        click: {};
      },
    },
  },
  onboarding: {
    [formatted]: "Onboarding",
    createAccount: {
      [formatted]: "Create Account",
      [actions]: {} as AsyncActions<{}> & {
        success: {
          authenticated: boolean;
        };
      },
      submitButton: {
        [formatted]: "Submit Button",
        [actions]: {} as {
          click: {};
        },
      },
    },
    emailConfirmation: {
      [formatted]: "Email Confirmation",
      [actions]: {} as {
        impression: {
          status: "success" | "invalid" | "error";
        };
      },
    },
    piiEntry: {
      [formatted]: "PII Entry",
      continueButton: {
        [formatted]: "Continue Button",
        [actions]: {} as {
          click: {
            step:
              | "Name"
              | "Phone"
              | "Phone code"
              | "Birthdate"
              | "Address"
              | "National ID"
              | "Review";
          };
        },
      },
      continue: {
        [formatted]: "Continue",
        [actions]: {} as AsyncActions<{
          step:
            | "Name"
            | "Phone"
            | "Phone code"
            | "Birthdate"
            | "Address"
            | "National ID"
            | "Review";
        }>,
      },
      backButton: {
        [formatted]: "Back Button",
        [actions]: {} as {
          click: {};
        },
      },
    },
    idv: {
      [formatted]: "IDV",
      retryPii: {
        [formatted]: "Retry PII",
        [actions]: {} as {
          click: {};
        },
      },
      manualUpload: {
        [formatted]: "Manual Upload",
        retryButton: {
          [formatted]: "Retry Button",
          [actions]: {} as {
            click: {};
          },
        },
        reuploadButton: {
          [formatted]: "Re-upload Button",
          [actions]: {} as {
            click: {};
          },
        },
      },
      creditFreeze: {
        [formatted]: "Credit Freeze",
        retryButton: {
          [formatted]: "Retry Button",
          [actions]: {} as {
            click: {};
          },
        },
      },
    },
  },
  hdyhauSurvey: {
    [formatted]: "HDYHAU Survey",
    [actions]: {} as {
      impression: {};
    },
    submitButton: {
      [formatted]: "Submit Button",
      [actions]: {} as {
        click: {
          option: string;
          followup: string;
        };
      },
    },
  },
  creditLine: {
    [formatted]: "Credit Line",
    open: {
      [formatted]: "Open",
      [actions]: {} as AsyncActions<{
        agreementId: CreditLine.Agreement.Id;
      }>,
    },
    payment: {
      [formatted]: "Payment",
      [actions]: {} as AsyncActions<{
        amountCents: number;
      }>,
    },
    close: {
      [formatted]: "Close",
      [actions]: {} as AsyncActions<{}>,
    },
  },
  paymentMethod: {
    [formatted]: "Payment Method",
    add: {
      [formatted]: "Add",
      [actions]: {} as AsyncActions<{}>,
    },
    remove: {
      [formatted]: "Remove",
      [actions]: {} as AsyncActions<{}>,
    },
  },
  subscription: {
    [formatted]: "Subscription",
    changePlan: {
      [formatted]: "Change Plan",
      [actions]: {} as AsyncActions<{
        from: Subscription.Tier;
        to: Subscription.Tier;
      }>,
    },
  },
} as const satisfies EventsSchema;

export default events;
